/**
 * MeuRoboNotificacoes — o dono escolhe quais fontes, e com que gravidade
 * mínima, o robô dele mostra (sub-projeto C, TASK-11255).
 *
 * DE ONDE (organização e tenants) deixou de ser por fonte: é o escopo do robô
 * inteiro, em "Meu robô › O que o robô mostra" (spec 2026-09-24, D2;
 * TASK-11447). O `scope_kind`/`scope_id` da assinatura do canal `robo` não é
 * mais lido pelo compositor, e esta tela deixou de oferecê-lo. A gravação
 * preserva o valor que já existia, para não mexer em dado declarado.
 *
 * O robô e' canal de PUXAR: assinar aqui NAO cria notificacao nova, so'
 * decide o que, do que a pessoa JA recebe, tambem aparece no display fisico.
 *
 * Sem PageHeader — vem do AppLayout via routeRegistry.
 */
import {
  Stack, Text, Switch, Select, Skeleton, Table, ScrollArea, Box,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconRobot } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { reciboDoDono, useMeuRobo, type AvisoDoAjuste, type Robo } from '@/api/robo'
import {
  notificationPreferencesApi,
  type Subscription,
  type SubscriptionSeverity,
  type SubscriptionUpdate,
} from '@/api/notification-preferences'
import { formatApiError } from '@/api/errors'
import { Callout, DataCard, EmptyState } from '@/components/ui'
import { PREFERENCE_SOURCES, SEVERITY_OPTIONS, sourceLabel } from '../notificacoes/preferencias-constants'
import { ReciboDoAviso } from './ReciboDoAviso'
import { O_QUE_A_FONTE_TRAZ } from './fontesDoRobo'
import { rotuloDoRobo } from './avisoDoAjuste'

const QUERY_KEY = ['me', 'notification-subscriptions'] as const
const CANAL = 'robo' as const

type SourceMap = Record<string, Subscription | undefined>

function buildSourceMap(items: Subscription[] | undefined): SourceMap {
  const map: SourceMap = {}
  if (!items) return map
  for (const s of items) {
    if (s.channel !== CANAL) continue
    if (s.event_type != null && s.event_type !== '') continue
    map[s.source] = s
  }
  return map
}

export function MeuRoboNotificacoes() {
  const { robo, carregando } = useMeuRobo()
  if (carregando) return <Text size="sm" c="dimmed">Carregando…</Text>
  if (!robo) {
    return (
      <EmptyState
        icon={IconRobot}
        title="Você ainda não tem um robô"
        description="Notificações no display são configuradas depois que um robô é atribuído a você."
      />
    )
  }
  return <FontesDoRobo robo={robo} />
}

/**
 * As fontes e a gravidade mínima, sem o estado vazio da página: é o passo
 * Alertas do wizard de recebimento e a seção `#alertas` da tela única.
 */
export function FontesDoRobo({ robo }: { robo: Robo }) {
  const qc = useQueryClient()

  const { data, isLoading: carregandoAssinaturas } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => notificationPreferencesApi.listSubscriptions(),
  })

  const sourceMap = useMemo(() => buildSourceMap(data?.items), [data])
  // A reação do robô ao último salvamento (spec robo-painel-de-ajustes 4.7).
  const [aviso, setAviso] = useState<AvisoDoAjuste | null>(null)

  const mutation = useMutation({
    mutationFn: (items: SubscriptionUpdate[]) =>
      notificationPreferencesApi.updateSubscriptions(items),
    onMutate: () => setAviso(null),
    onSuccess: (resposta) => {
      qc.invalidateQueries({ queryKey: QUERY_KEY })
      setAviso(resposta.aviso ?? null)
      notifications.show({ color: 'green', title: 'Salvo', message: 'Preferência do robô atualizada.' })
    },
    onError: (err: unknown) => {
      notifications.show({
        color: 'red',
        title: 'Não foi possível salvar',
        message: formatApiError(err, 'Tente de novo em instantes.'),
      })
    },
  })

  const salvar = (source: string, patch: Partial<SubscriptionUpdate>) => {
    const atual = sourceMap[source]
    mutation.mutate([{
      source,
      event_type: null,
      channel: CANAL,
      enabled: atual?.enabled ?? false,
      min_severity: atual?.min_severity ?? 'info',
      scope_kind: atual?.scope_kind ?? null,
      scope_id: atual?.scope_id ?? null,
      ...patch,
    }])
  }

  const handleToggle = (source: string, enabled: boolean) => salvar(source, { enabled })
  const handleSeveridade = (source: string, min_severity: SubscriptionSeverity) =>
    salvar(source, { min_severity })
  const carregando = carregandoAssinaturas

  return (
    <Stack gap="md">
      <Callout variant="info">
        O robô mostra o que você já recebe — assinar aqui não cria notificação nova.
      </Callout>
      <Callout variant="info">
        De qual organização e de quais tenants vale para todas as fontes, e se
        escolhe em Meu robô, no bloco “O que o robô mostra”.
      </Callout>
      <Callout variant="info">
        O visor é sem acento: títulos aparecem como <code>&quot;Servico critico&quot;</code>.
      </Callout>

      {aviso && (
        <ReciboDoAviso
          key={aviso.id ?? aviso.estado}
          aviso={aviso}
          caminho={aviso.id ? reciboDoDono(aviso.id) : null}
          robo={rotuloDoRobo(robo.hardware_model)}
        />
      )}

      <DataCard flush title="Fontes no display do robô">
        {carregando ? (
          <Stack gap="xs" p="md">
            <Skeleton h={32} />
            <Skeleton h={32} />
            <Skeleton h={32} />
          </Stack>
        ) : (
          <ScrollArea>
            <Box style={{ minWidth: 420 }}>
              <Table verticalSpacing="xs" horizontalSpacing="sm" highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ width: 260 }}>Fonte</Table.Th>
                    <Table.Th style={{ width: 90, textAlign: 'center' }}>No robô</Table.Th>
                    <Table.Th>Severidade mín.</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {PREFERENCE_SOURCES.map((source) => {
                    const sub = sourceMap[source]
                    return (
                      <Table.Tr key={source}>
                        <Table.Td>
                          <Text size="sm">{sourceLabel(source)}</Text>
                          {O_QUE_A_FONTE_TRAZ[source] && (
                            <Text size="xs" c="dimmed">{O_QUE_A_FONTE_TRAZ[source]}</Text>
                          )}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'center' }}>
                          <Switch
                            checked={sub?.enabled ?? false}
                            disabled={mutation.isPending}
                            onChange={(e) => handleToggle(source, e.currentTarget.checked)}
                            aria-label={`${sourceLabel(source)} no robô`}
                          />
                        </Table.Td>
                        <Table.Td>
                          <Select
                            size="xs"
                            data={SEVERITY_OPTIONS}
                            value={sub?.min_severity ?? 'info'}
                            disabled={!sub?.enabled || mutation.isPending}
                            onChange={(v) => v && handleSeveridade(source, v as SubscriptionSeverity)}
                            allowDeselect={false}
                            comboboxProps={{ withinPortal: true }}
                          />
                        </Table.Td>
                      </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            </Box>
          </ScrollArea>
        )}
      </DataCard>
    </Stack>
  )
}
